// @ts-nocheck — close port of upstream three/addons/tsl/display/FXAANode.js,
// which ships without a .d.ts. The TSL type definitions are much stricter
// than the upstream runtime contracts (e.g. overloads reject args the JS
// runtime happily accepts), so checking this file would mean casting every
// single TSL call through `any`. Upstream's JS code is well-exercised; we
// trust it rather than re-type it.
//
// Local copy of three/addons/tsl/display/FXAANode.js with the three
// quality thresholds exposed as uniforms instead of baked `float(...)`
// constants. Everything else matches the addon — this is just the
// lever we need to drive a runtime quality-preset dropdown.
//
// Thresholds (NVIDIA FXAA 3.11 naming):
//   contrastThreshold — absolute luma contrast below which a pixel
//                       is skipped. Smaller = more pixels processed.
//   relativeThreshold — scales the contrast skip by local luma.
//                       Smaller = more pixels processed on bright areas.
//   subpixelBlending  — how much to blend toward neighborhood average
//                       after edge detection. 0 = hard edges, 1 = soft.
import { Vector2, TempNode } from 'three/webgpu';
import type { Node } from 'three/webgpu';
import {
  Fn, uniformArray, select, float, NodeUpdateType, uv, dot, clamp,
  uniform, convertToTexture, smoothstep, bool, vec2, vec3, If, Loop,
  max, min, Break, abs
} from 'three/tsl';

class FXAATunableNode extends TempNode {

  static get type() { return 'FXAATunableNode'; }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private textureNode: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private _invSize: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public uContrast: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public uRelative: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public uSubpixel: any;

  // Cached input texture size from the last `updateBefore`. The texture
  // only resizes on viewport resize, so refreshing the inv-size uniform
  // once per actual size change beats the per-frame Vec2 division.
  private _lastW = 0;
  private _lastH = 0;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  constructor(textureNode: any, contrast: number, relative: number, subpixel: number) {
    super('vec4');
    this.textureNode = textureNode;
    this.updateBeforeType = NodeUpdateType.FRAME;
    this._invSize = uniform(new Vector2());
    this.uContrast = uniform(contrast);
    this.uRelative = uniform(relative);
    this.uSubpixel = uniform(subpixel);
  }

  updateBefore(): void {
    const map = this.textureNode.value;
    const w = map.image.width;
    const h = map.image.height;
    if (w !== this._lastW || h !== this._lastH) {
      this._invSize.value.set(1 / w, 1 / h);
      this._lastW = w;
      this._lastH = h;
    }
  }

  setup(): Node<'vec4'> {
    const textureNode = this.textureNode.bias(-100);
    const uvNode = textureNode.uvNode || uv();

    const EDGE_STEP_COUNT = float(6);
    const EDGE_GUESS = float(8.0);
    const EDGE_STEPS = uniformArray([1.0, 1.5, 2.0, 2.0, 2.0, 4.0]);

    const _ContrastThreshold = this.uContrast;
    const _RelativeThreshold = this.uRelative;
    const _SubpixelBlending  = this.uSubpixel;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const Sample = Fn(([u]: any) => textureNode.sample(u));
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const SampleLuminance = Fn(([u]: any) => dot(Sample(u).rgb, vec3(0.3, 0.59, 0.11)));
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const SampleLuminanceOffset = Fn(([texSize, u, ou, ov]: any) => {
      const shifted = u.add(texSize.mul(vec2(ou, ov)));
      return SampleLuminance(shifted);
    });

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const ShouldSkipPixel = (l: any) => {
      const threshold = max(_ContrastThreshold, _RelativeThreshold.mul(l.highest));
      return l.contrast.lessThan(threshold);
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const SampleLuminanceNeighborhood = (texSize: any, u: any) => {
      const m = SampleLuminance(u);
      const n  = SampleLuminanceOffset(texSize, u,  0.0, -1.0);
      const e  = SampleLuminanceOffset(texSize, u,  1.0,  0.0);
      const s  = SampleLuminanceOffset(texSize, u,  0.0,  1.0);
      const w  = SampleLuminanceOffset(texSize, u, -1.0,  0.0);
      const ne = SampleLuminanceOffset(texSize, u,  1.0, -1.0);
      const nw = SampleLuminanceOffset(texSize, u, -1.0, -1.0);
      const se = SampleLuminanceOffset(texSize, u,  1.0,  1.0);
      const sw = SampleLuminanceOffset(texSize, u, -1.0,  1.0);
      const highest = max(s, e, n, w, m);
      const lowest  = min(s, e, n, w, m);
      const contrast = highest.sub(lowest);
      return { m, n, e, s, w, ne, nw, se, sw, highest, lowest, contrast };
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const DeterminePixelBlendFactor = (l: any) => {
      let f = float(2.0).mul(l.s.add(l.e).add(l.n).add(l.w));
      f = f.add(l.se.add(l.sw).add(l.ne).add(l.nw));
      f = f.mul(1.0 / 12.0);
      f = abs(f.sub(l.m));
      f = clamp(f.div(max(l.contrast, 0)), 0.0, 1.0);
      const blendFactor = smoothstep(0.0, 1.0, f);
      return blendFactor.mul(blendFactor).mul(_SubpixelBlending);
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const DetermineEdge = (texSize: any, l: any) => {
      const horizontal =
        abs(l.s.add(l.n).sub(l.m.mul(2.0))).mul(2.0).add(
          abs(l.se.add(l.ne).sub(l.e.mul(2.0))).add(
            abs(l.sw.add(l.nw).sub(l.w.mul(2.0)))
          )
        );
      const vertical =
        abs(l.e.add(l.w).sub(l.m.mul(2.0))).mul(2.0).add(
          abs(l.se.add(l.sw).sub(l.s.mul(2.0))).add(
            abs(l.ne.add(l.nw).sub(l.n.mul(2.0)))
          )
        );
      const isHorizontal = horizontal.greaterThanEqual(vertical);
      const pLuminance = select(isHorizontal, l.s, l.e);
      const nLuminance = select(isHorizontal, l.n, l.w);
      const pGradient = abs(pLuminance.sub(l.m));
      const nGradient = abs(nLuminance.sub(l.m));
      const pixelStep = select(isHorizontal, texSize.y, texSize.x).toVar();
      const oppositeLuminance = float().toVar();
      const gradient = float().toVar();
      If(pGradient.lessThan(nGradient), () => {
        pixelStep.assign(pixelStep.negate());
        oppositeLuminance.assign(nLuminance);
        gradient.assign(nGradient);
      }).Else(() => {
        oppositeLuminance.assign(pLuminance);
        gradient.assign(pGradient);
      });
      return { isHorizontal, pixelStep, oppositeLuminance, gradient };
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const DetermineEdgeBlendFactor = (texSize: any, l: any, e: any, u: any) => {
      const uvEdge = u.toVar();
      const edgeStep = vec2().toVar();
      If(e.isHorizontal, () => {
        uvEdge.y.addAssign(e.pixelStep.mul(0.5));
        edgeStep.assign(vec2(texSize.x, 0.0));
      }).Else(() => {
        uvEdge.x.addAssign(e.pixelStep.mul(0.5));
        edgeStep.assign(vec2(0.0, texSize.y));
      });
      const edgeLuminance = l.m.add(e.oppositeLuminance).mul(0.5);
      const gradientThreshold = e.gradient.mul(0.25);
      const puv = uvEdge.add(edgeStep.mul(EDGE_STEPS.element(0))).toVar();
      const pLuminanceDelta = SampleLuminance(puv).sub(edgeLuminance).toVar();
      const pAtEnd = abs(pLuminanceDelta).greaterThanEqual(gradientThreshold).toVar();
      Loop({ start: 1, end: EDGE_STEP_COUNT }, ({ i }) => {
        If(pAtEnd, () => { Break(); });
        puv.addAssign(edgeStep.mul(EDGE_STEPS.element(i)));
        pLuminanceDelta.assign(SampleLuminance(puv).sub(edgeLuminance));
        pAtEnd.assign(abs(pLuminanceDelta).greaterThanEqual(gradientThreshold));
      });
      If(pAtEnd.not(), () => { puv.addAssign(edgeStep.mul(EDGE_GUESS)); });
      const nuv = uvEdge.sub(edgeStep.mul(EDGE_STEPS.element(0))).toVar();
      const nLuminanceDelta = SampleLuminance(nuv).sub(edgeLuminance).toVar();
      const nAtEnd = abs(nLuminanceDelta).greaterThanEqual(gradientThreshold).toVar();
      Loop({ start: 1, end: EDGE_STEP_COUNT }, ({ i }) => {
        If(nAtEnd, () => { Break(); });
        nuv.subAssign(edgeStep.mul(EDGE_STEPS.element(i)));
        nLuminanceDelta.assign(SampleLuminance(nuv).sub(edgeLuminance));
        nAtEnd.assign(abs(nLuminanceDelta).greaterThanEqual(gradientThreshold));
      });
      If(nAtEnd.not(), () => { nuv.subAssign(edgeStep.mul(EDGE_GUESS)); });
      const pDistance = float().toVar();
      const nDistance = float().toVar();
      If(e.isHorizontal, () => {
        pDistance.assign(puv.x.sub(u.x));
        nDistance.assign(u.x.sub(nuv.x));
      }).Else(() => {
        pDistance.assign(puv.y.sub(u.y));
        nDistance.assign(u.y.sub(nuv.y));
      });
      const shortestDistance = float().toVar();
      const deltaSign = bool().toVar();
      If(pDistance.lessThanEqual(nDistance), () => {
        shortestDistance.assign(pDistance);
        deltaSign.assign(pLuminanceDelta.greaterThanEqual(0.0));
      }).Else(() => {
        shortestDistance.assign(nDistance);
        deltaSign.assign(nLuminanceDelta.greaterThanEqual(0.0));
      });
      const blendFactor = float().toVar();
      If(deltaSign.equal(l.m.sub(edgeLuminance).greaterThanEqual(0.0)), () => {
        blendFactor.assign(0.0);
      }).Else(() => {
        blendFactor.assign(float(0.5).sub(shortestDistance.div(pDistance.add(nDistance))));
      });
      return blendFactor;
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const ApplyFXAA = Fn(([u, texSize]: any) => {
      const luminance = SampleLuminanceNeighborhood(texSize, u);
      If(ShouldSkipPixel(luminance), () => { return Sample(u); });
      const pixelBlend = DeterminePixelBlendFactor(luminance);
      const edge = DetermineEdge(texSize, luminance);
      const edgeBlend = DetermineEdgeBlendFactor(texSize, luminance, edge, u);
      const finalBlend = max(pixelBlend, edgeBlend);
      const finalUv = u.toVar();
      If(edge.isHorizontal, () => { finalUv.y.addAssign(edge.pixelStep.mul(finalBlend)); })
       .Else(() => { finalUv.x.addAssign(edge.pixelStep.mul(finalBlend)); });
      return Sample(finalUv);
    }).setLayout({
      name: 'FxaaTunablePixelShader',
      type: 'vec4',
      inputs: [
        { name: 'u', type: 'vec2' },
        { name: 'texSize', type: 'vec2' },
      ]
    });

    return Fn(() => ApplyFXAA(uvNode, this._invSize))() as unknown as Node<'vec4'>;
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const fxaaTunable = (node: any, contrast: number, relative: number, subpixel: number) =>
  new FXAATunableNode(convertToTexture(node), contrast, relative, subpixel);

export type FXAATunableLive = {
  uContrast: { value: number };
  uRelative: { value: number };
  uSubpixel: { value: number };
};

// Preset → (contrastThreshold, relativeThreshold, subpixelBlending).
// Matches NVIDIA FXAA 3.11 quality tiers roughly; 'ultra' is what the
// upstream three.js addon uses, so switching to it preserves prior
// rendering behavior exactly.
export const FXAA_PRESETS = {
  low:    { contrast: 0.0833, relative: 0.166,  subpixel: 0.25 },
  medium: { contrast: 0.0625, relative: 0.125,  subpixel: 0.50 },
  high:   { contrast: 0.0312, relative: 0.063,  subpixel: 0.75 },
  ultra:  { contrast: 0.0312, relative: 0.063,  subpixel: 1.00 }
} as const;

export type FXAAPreset = keyof typeof FXAA_PRESETS;
